import Layout from 'components/layout';

import React, { FC, PropsWithChildren } from 'react';

import { graphql, HeadFC } from 'gatsby';
import {
  Box,
  ComponentImage,
  ComponentLink,
  Container,
  FeaturedVideo,
  Hero,
  VideoProps,
  SEO,
} from '@lucid/components-react';
import { getEntityFields } from '@lucid/utils';
import type {
  Drupal_NodeLeadership,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphFeaturedVideo,
  Drupal_ParagraphFlexiCustomerLogos,
  Drupal_ParagraphNewHeroSection,
  Drupal_ParagraphPeopleSection,
} from 'generated/drupal-graphql';
import { mqMin } from '@lucid/theme';

import type { LucidTheme, Template } from 'types/types';
import PeopleSection from 'components/people/people-section';
import FlexiCustomerLogos from 'components/flexi/flexi-customer-logos/flexi-customer-logos';
import { PersonData } from 'components/people/person';

const Leadership: FC<PropsWithChildren<Template.PageProps>> = ({ data, pageContext }) => {
  const { shortLangPrefix, pageType, nid, shouldHideSsr } = pageContext;
  const { drupal } = data;
  const node = drupal.nodeById as Drupal_NodeLeadership;
  const { entityTranslations } = getEntityFields(node);
  const { fieldHeroSection, fieldLeadershipTeam, fieldBoardOfDirectors, fieldFeaturedVideo, fieldCustomerLogos } = node;

  // Hero
  const heroSection = fieldHeroSection?.entity as Drupal_ParagraphNewHeroSection;
  const heroCtaLinkButton = heroSection.fieldCtaButtons?.[0]?.entity as Drupal_ParagraphCtaButton;
  const heroButton: ComponentLink = {
    linkUrl: heroCtaLinkButton?.fieldButtonLink || '',
    linkText: heroCtaLinkButton?.fieldButtonText || '',
    isSecondary: heroCtaLinkButton?.fieldIsSecondary,
  };

  // Leadership Team
  const {
    fieldHeader: leadershipHeading,
    fieldFlexiSubheader: leadershipSubheading,
    fieldPeople: leadershipPeopleEntity,
  } = fieldLeadershipTeam?.entity as Drupal_ParagraphPeopleSection;
  const leadershipPeople = leadershipPeopleEntity?.map(person => person?.entity) as unknown as PersonData[];

  // Board of Directors
  const {
    fieldHeader: boardHeading,
    fieldFlexiSubheader: boardSubheading,
    fieldPeople: boardPeopleEntity,
  } = fieldBoardOfDirectors?.entity as Drupal_ParagraphPeopleSection;
  const boardPeople = boardPeopleEntity?.map(person => person?.entity) as unknown as PersonData[];

  // Featured Video
  const featuredVideo = fieldFeaturedVideo?.entity as Drupal_ParagraphFeaturedVideo;
  const featuredVideos = featuredVideo?.fieldVideo?.map(video => video.entity) as unknown as VideoProps[];
  const featuredVideoFormattedBullets = featuredVideo.fieldBulletPointsFormatted?.map(
    bullet => bullet?.processed,
  ) as string[];

  return (
    <Layout
      hasNavbar
      entityTranslations={entityTranslations}
      nid={nid}
      pageType={pageType}
      shortLangPrefix={shortLangPrefix}
      shouldHideSsr={shouldHideSsr}
    >
      {heroSection && (
        <Box
          css={(theme: LucidTheme) => ({
            background: theme.colors.coolGrey[100],
            padding: `0 ${theme.space.s8}px`,
            display: 'flex',
            justifyContent: 'center',
          })}
        >
          <Hero
            hasDarkBackground
            css={(theme: LucidTheme) => ({
              maxWidth: '580px',
              padding: `${theme.space.s120}px 0`,
              [mqMin.tabletSmall]: {
                '& p': {
                  fontSize: theme.fontSizes.M,
                },
              },
              [mqMin.tablet]: {
                padding: `${theme.space.s120}px 0`,
              },
            })}
            cta={heroButton}
            header={heroSection.fieldHeader as string}
            image={
              {
                imageSrc: heroSection.fieldImageLink,
                imageAlt: '',
              } as ComponentImage
            }
            subheader={(heroSection.fieldSubheader as string) || (heroSection.fieldLongSubheader as string)}
            subheaderVariant="paragraphL300"
          />
        </Box>
      )}
      <Container>
        <PeopleSection
          colorScheme={['indigo', 'blue', 'pink']}
          css={(theme: LucidTheme) => ({
            paddingTop: theme.space.s120,
          })}
          heading={leadershipHeading}
          people={leadershipPeople}
          showMoreLimit={6}
          subheading={leadershipSubheading}
          variant="threeAcross"
        />
        <PeopleSection
          colorScheme={['coolGrey']}
          css={(theme: LucidTheme) => ({
            paddingTop: theme.space.s120,
          })}
          heading={boardHeading}
          people={boardPeople}
          subheading={boardSubheading}
          variant="fourAcross"
        />
      </Container>
      {featuredVideo.fieldHeader && featuredVideos && (
        <Box
          css={(theme: LucidTheme) => ({
            marginTop: theme.space.s120,
            [mqMin.tabletSmall]: {
              background: theme.colors.primary,
              padding: `${theme.space.s80}px ${theme.space.s8}px`,
            },
            [mqMin.tabletLandscape]: {
              background: theme.colors.primary,
              padding: theme.space.s120,
            },
          })}
        >
          <FeaturedVideo
            cta={
              {
                linkUrl: featuredVideo?.fieldCta?.url?.path,
                linkText: featuredVideo?.fieldCta?.title,
              } as ComponentLink
            }
            header={featuredVideo.fieldHeader as string}
            items={
              featuredVideoFormattedBullets.length
                ? featuredVideoFormattedBullets
                : (featuredVideo.fieldBulletPoints as string[])
            }
            videos={featuredVideos}
          />
        </Box>
      )}
      <Container
        css={{
          maxWidth: '435px',
          [mqMin.tabletSmall]: {
            maxWidth: '90rem',
          },
        }}
      >
        <FlexiCustomerLogos
          entity={fieldCustomerLogos?.entity as Drupal_ParagraphFlexiCustomerLogos}
          headingVariant="headline2XL"
        />
      </Container>
    </Layout>
  );
};

export default Leadership;

export const Head: HeadFC<Template.PageProps['data'], Template.PageProps['pageContext']> = ({ data, location }) => {
  const { drupal } = data;
  const node = drupal.nodeById as Drupal_NodeLeadership;
  const { entityMetatags, entitySchemaMetatags } = getEntityFields(node);

  return <SEO entityMetatags={entityMetatags} entitySchemaMetatags={entitySchemaMetatags} location={location} />;
};

export const query = graphql`
  query ($nid: String!, $LanguageId: Drupal_LanguageId!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeLeadership {
          ...entityFields
          fieldHeroSection {
            entity {
              ... on Drupal_ParagraphNewHeroSection {
                fieldHeader
                fieldImageLink
                fieldLongSubheader
                fieldSubheader
                fieldTitlePiece
                fieldCtaButtons {
                  entity {
                    ... on Drupal_ParagraphCtaButton {
                      fieldButtonLink
                      fieldButtonText
                      fieldIsSecondary
                    }
                  }
                }
              }
            }
          }
          fieldLeadershipTeam {
            entity {
              ... on Drupal_ParagraphPeopleSection {
                fieldHeader
                fieldFlexiSubheader
                fieldPeople {
                  entity {
                    ... on Drupal_ParagraphPerson {
                      id
                      fieldName
                      fieldTitle
                      fieldImageUrl
                      fieldCompanyName
                      fieldBio
                    }
                  }
                }
              }
            }
          }
          fieldBoardOfDirectors {
            entity {
              ... on Drupal_ParagraphPeopleSection {
                fieldHeader
                fieldFlexiSubheader
                fieldPeople {
                  entity {
                    ... on Drupal_ParagraphPerson {
                      id
                      fieldTitle
                      fieldName
                      fieldImageUrl
                      fieldCompanyName
                      fieldBio
                    }
                  }
                }
              }
            }
          }
          fieldCustomerLogos {
            entity {
              ... on Drupal_ParagraphFlexiCustomerLogos {
                fieldFlexiHeaderJustify {
                  entity {
                    entityLabel
                  }
                }
                fieldCustomerLogosHeader {
                  processed
                }
                fieldCustomerLogosLink {
                  title
                  url {
                    path
                  }
                }
                fieldLogosSection {
                  entity {
                    ... on Drupal_ParagraphExternalLogoSection {
                      fieldExternalLogos {
                        entity {
                          ... on Drupal_ParagraphExternalLogo {
                            fieldLogoSlug
                          }
                        }
                      }
                      fieldLogoLayoutVariant
                      fieldLogosColor
                      fieldRowHeight
                    }
                  }
                }
              }
            }
          }
          fieldFeaturedVideo {
            entity {
              ... on Drupal_ParagraphFeaturedVideo {
                fieldBulletPoints
                fieldBulletPointsFormatted {
                  processed
                }
                fieldCta {
                  title
                  url {
                    path
                  }
                }
                fieldHeader
                fieldVideo {
                  entity {
                    ... on Drupal_ParagraphVideo {
                      videoLinkPlaceholder: fieldVideoBackgroundImage
                      videoLinkPlacholderAlt: fieldVideoBackgroundImageAlt
                      videoLink: fieldVideoLink
                      videoDescription: fieldVideoDescription
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
